import { FileExcelOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Checkbox, Col, Dropdown, Input, Menu, message, Pagination, Popconfirm, Row, Space, Table, Tooltip} from 'antd';
import { jsPDF } from 'jspdf';
import { font } from '../../Common/font';
import 'jspdf-autotable';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';

import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as PlanPrintIcon } from '../../Images/SimplePrintIcon.svg';
import { ReactComponent as ViewIcon } from '../../Images/ViewIcon.svg';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProformaList, archiveProforma } from '../../slices/proforma';
import { ReactComponent as PrintIcons } from '../../Images/PrintIcon.svg';
import dayjs from 'dayjs';
import moment from 'moment';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import { createActivityLog } from '../../slices/activity-log';
import eventBus from '../../utils/eventBus';
import { CSVLink } from 'react-csv';
import logo from '../../Images/alux-logo.png';

function ProformaList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(
    localStorage.getItem('ProformaList') || '',
  );
  const [inputValue, setInputValue] = useState(
    localStorage.getItem('ProformaList') || '',
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { user: currentUser } = useSelector(state => state.auth);

  const { data = [], totalRecord, status } = useSelector(state => state.proforma);
  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
  }));

  const ipAddress = useSelector(state => state.ip.ipAddress);
  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [ipAddress, dispatch]);

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(archiveProforma(id))
      .unwrap()
      .then(() => {
        message.success('Proforma Archive Successfully!');
        dispatch(
          fetchProformaList({
            page,
            pagesize,
            ...(search && { search: search }),
            ...(orderBy && { ordering: orderBy }),
          }),
        );
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error); // Handle string error
        } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    setOrderBy('');
  };

  const fetchPerforma = useCallback(() => {
    dispatch(
      fetchProformaList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);

  useEffect(() => {
    fetchPerforma();
  }, [fetchPerforma]);

  useEffect(() => {
    const handleReload = () => {

      fetchPerforma();
    };

    eventBus.on("financialYear:refresh", handleReload);

    return () => {
      eventBus.off("financialYear:refresh", handleReload);
    };
  }, [fetchPerforma]);

  const handleEdit = record => {
    navigate(`/edit-proforma/${record.id}`);
  };

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 100,
      render: (text, record, index) => {
        return (page - 1) * pagesize + index + 1;
      },
    },
    {
      title: 'Proforma Date',
      dataIndex: 'proforma_date',
      key: 'proforma_date',
      sorter: true,
      render: proforma_date => {
        return (
          <span>
            {proforma_date ? dayjs(proforma_date).format('DD-MM-YYYY') : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Proforma No.',
      dataIndex: 'proforma_no',
      key: 'proforma_no',
      sorter: true,
    },
    {
      title: 'Work Order No.',
      dataIndex: 'workorder_order_no', // Use unique dataIndex
      key: 'workorder_order_no',

      render: (_, record) => {
        return <span>{record?.workorder_no || 'N/A'}</span>;
      },
    },
    {
      title: 'Customer Name',
      dataIndex: 'workorder_customer_name', // Use unique dataIndex
      key: 'workorder_customer_name',

      render: (_, record) => {
        return (
          <span>{record?.customer_name || 'N/A'}</span>
        );
      },
    },
    {
      title: 'Freight Charges',
      dataIndex: 'freight_charges',
      key: 'freight_charges',
      sorter: true,
      render: (_, record) => {
        return <span>{record?.freight_charges || 'N/A'}</span>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: record => {
        return record ? (
          <span>{dayjs(record).format('DD-MM-YYYY HH:mm')}</span>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? `${record.first_name} ${record.last_name}` : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && record.first_name?.trim() && record.last_name?.trim()
              ? `${record.first_name} ${record.last_name}`
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: record => (
        <Space>
          {currentUser.permissions.permissions.proforma.includes(
            'view_proforma',
          ) && (
              <Tooltip title="View">
                <Link to={`/view-proforma/${record.id}`}>
                  <ViewIcon />
                </Link>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.proforma.includes(
            'print_proforma_copy',
          ) && (
              <Tooltip title="Print">
                <Link
                  to={`/print-proforma/${record.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PlanPrintIcon />
                </Link>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.proforma.includes(
            'change_proforma',
          ) && (
              <Tooltip placement="top" title={'Edit'}>
                <span
                  onClick={() => handleEdit(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <EditIcon />
                </span>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.proforma.includes(
            'delete_proforma',
          ) && (
              <Tooltip title="Archive">
                <Popconfirm
                  title="Are you sure you want to archive this record?"
                  onConfirm={() => handleDelete(record.id)} // Correctly access `id`
                  onCancel={() => message.info('Action cancelled')}
                  okText="Yes"
                  cancelText="No"
                >
                  <Link>
                    <Archive />
                  </Link>
                </Popconfirm>
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];

  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'proforma_date',
    'proforma_no',
    'order_no',
    'workorder_order_no',
    'workorder_customer_name',
    'action',
    'freight_charges',
    'Sr. No.',
    'Profile Number.',
    'Customer Reference No.',
    'Alloy',
    'Temper',
    'JobWork/Value Addition',
    'Length(mm)',
    'Pieces',
    'Net Weight(kg)',
    'Nalco Rate',
    'Packed Weight (kg)',
    'Dispatch Qty',
  ]);

  const dynamicColumns = columns.filter(column =>
    checkedList.includes(column.dataIndex || column.key),
  );
  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'SrNo' && sortOrder === 'ascend') {
      sortingEnumKey = 'SrNo';
    }
    if (sortField === 'SrNo' && sortOrder === 'descend') {
      sortingEnumKey = '-SrNo';
    }
    if (sortField === 'workorder__order_date' && sortOrder === 'ascend') {
      sortingEnumKey = 'workorder__order_date';
    }
    if (sortField === 'workorder__order_date' && sortOrder === 'descend') {
      sortingEnumKey = '-workorder__order_date';
    }
    if (sortField === 'workorder_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'workorder_no';
    }
    if (sortField === 'workorder_no' && sortOrder === 'descend') {
      sortingEnumKey = '-workorder_no';
    }
    if (sortField === 'workorder_customer_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'workorder_customer_name';
    }
    if (sortField === 'workorder_customer_name' && sortOrder === 'descend') {
      sortingEnumKey = '-workorder_customer_name';
    }
    if (sortField === 'packing_type' && sortOrder === 'ascend') {
      sortingEnumKey = 'packing_type';
    }
    if (sortField === 'packing_type' && sortOrder === 'descend') {
      sortingEnumKey = '-packing_type';
    }
    if (sortField === 'proforma_date' && sortOrder === 'ascend') {
      sortingEnumKey = 'proforma_date';
    }
    if (sortField === 'proforma_date' && sortOrder === 'descend') {
      sortingEnumKey = '-proforma_date';
    }
    if (sortField === 'freight_charges' && sortOrder === 'ascend') {
      sortingEnumKey = 'freight_charges';
    }
    if (sortField === 'freight_charges' && sortOrder === 'descend') {
      sortingEnumKey = '-freight_charges';
    }

    if (sortField === 'advance_amount' && sortOrder === 'descend') {
      sortingEnumKey = '-advance_amount';
    }
    if (sortField === 'advance_amount' && sortOrder === 'ascend') {
      sortingEnumKey = 'advance_amount';
    }
    if (sortField === 'delivery_schedule' && sortOrder === 'descend') {
      sortingEnumKey = '-delivery_schedule';
    }
    if (sortField === 'delivery_schedule' && sortOrder === 'descend') {
      sortingEnumKey = '-delivery_schedule';
    }
    if (sortField === 'weight_range' && sortOrder === 'ascend') {
      sortingEnumKey = 'weight_range';
    }
    if (sortField === 'weight_range' && sortOrder === 'descend') {
      sortingEnumKey = '-weight_range';
    }
    if (sortField === 'proforma_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'proforma_no';
    }
    if (sortField === 'proforma_no' && sortOrder === 'descend') {
      sortingEnumKey = '-proforma_no';
    }
    if (sortField === 'created_by' && sortOrder === 'ascend') {
      sortingEnumKey = 'created_by__first_name';
    }
    if (sortField === 'created_by' && sortOrder === 'descend') {
      sortingEnumKey = '-created_by__first_name';
    }
    if (sortField === 'updated_by' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_by__first_name';
    }
    if (sortField === 'updated_by' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_by__first_name';
    }
    return sortingEnumKey;
  };

  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Production',
        ip_address: ipAddress,
        discription: 'Production excel download successfully!',
      }),
    );
  };

  const csvheaders = [
    { label: 'Sr. No', key: 'SrNo' },
    { label: 'Proforma Date', key: 'proforma_date' },
    { label: 'Proforma No.', key: 'proforma_no' },
    { label: 'Work Order No.', key: 'workorder_order_no' },
    { label: 'Customer Name', key: 'workorder_customer_name' },
    { label: 'Freight Charges', key: 'freight_charges' },
    { label: 'created At', key: 'created_at' },
    { label: 'created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];

  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const csvData = data.map((item, index) => ({
    SrNo: index + 1,
    proforma_date: item.proforma_date || 'N/A',
    proforma_no: item.proforma_no || 'N/A',
    workorder_order_no: item.workorder_no || 'N/A',
    workorder_customer_name: item.customer_name || 'N/A',
    freight_charges: item.freight_charges || 'N/A',
    created_at: item.created_at
      ? moment(item.created_at).format('DD-MM-YYYY')
      : 'N/A',
    created_by: item.created_by
      ? `${item.created_by.first_name} ${item.created_by.last_name}`
      : 'N/A',
    updated_at: item.updated_at
      ? moment(item.updated_at).format('DD-MM-YYYY')
      : 'N/A',
    updated_by: item.updated_by
      ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
      : 'N/A',
  }));

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
  };
  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={checkedValues => {
            handleColumnVisibilityChange(checkedValues);
          }}
        >
          <Row gutter={16}>
            <Col xs={12} md={12}>
              <Checkbox value="srNo" disabled>
                Sr.No.
              </Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="proforma_date">Proforma Date</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="proforma_no">Proforma No</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="workorder_order_no">WorkOrder No</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="workorder_customer_name">Customer Name</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="freight_charges">Freight Charges</Checkbox>
            </Col>

            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const debounceTimeout = useRef(null);
  const handleSearch = e => {
    const value = e.target.value;
    setInputValue(value);
    setSearch(value);
    localStorage.setItem('ProformaList', value);
  };

  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    setOrderBy(null); // if applicable

    localStorage.removeItem('ProformaList');
    dispatch(fetchProformaList({ page: 1, pagesize }));
  };

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handlePrint = record => {
    const doc = new jsPDF('l', 'pt', 'a4');
    let y = 20;
    doc.setFontSize(16);
    doc.text('Proforma Details', doc.internal.pageSize.width / 2, y, {
      align: 'center',
    });
    y += 40;
    doc.setFontSize(12);
    const leftMargin = 40;
    const labelWidth = 110;
    const leftFields = [
      {
        label: 'Proforma Date:',
        value: record?.proforma_date
          ? dayjs(record.proforma_date).format('DD-MM-YYYY')
          : 'N/A',
      },
      {
        label: 'Proforma No.:',
        value: record?.proforma_no || 'N/A',
      },
      {
        label: 'WorkOrder No:',
        value: record?.workorder.order_no || 'N/A',
      },
      {
        label: 'Weight Range:',
        value: record?.weight_range || 'N/A',
      },
      {
        label: 'Advance amount:',
        value: record?.advance_amount || 'N/A',
      },
    ];
    const rightFields = [
      {
        label: 'Customer Name:',
        value: record?.workorder?.bill_to.customer_name || 'N/A',
      },
      {
        label: 'Freight Charges:',
        value: record?.freight_charges || 'N/A',
      },
      {
        label: 'Adavance amount:',
        value: record?.advance_amount || 'N/A',
      },

      {
        label: 'Delivery Schedule:',
        value: record?.delivery_schedule || 'N/A',
      },
      {
        label: 'Remarks:',
        value: record?.remarks || 'N/A',
      },
    ];
    let currentY = y;
    leftFields.forEach(field => {
      doc.text(field.label, leftMargin, currentY);
      doc.text(field.value.toString(), leftMargin + labelWidth, currentY);
      currentY += 20;
    });
    currentY = y;
    const rightColX = doc.internal.pageSize.width / 2 + 10;
    rightFields.forEach(field => {
      doc.text(field.label, rightColX, currentY);
      doc.text(field.value.toString(), rightColX + labelWidth, currentY);
      currentY += 20;
    });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    doc.setFontSize(12);

    const pdfColumns = [
      'Sr. No.',
      'Profile Number.',
      'Customer Reference No.',
      'Alloy',
      'Temper',
      'JobWork/Value Addition',
      'Length(mm)',
      'Pieces',
      'Net Weight(kg)',
      'Nalco Rate',
      'Packed Weight (kg)',
      'Dispatch Qty',
    ];
    const rows = record?.workorder.work_order_details.map((item, index) => [
      index + 1,
      item?.die_profile.die_number || 'N/A',
      item?.customer_reference_number || 'N/A',
      item?.alloy.name || 'N/A',
      item?.temper.name || 'N/A',
      item?.jobwork || 'N/A',
      item?.length || 'N/A',
      item?.pieces || 'N/A',
      item?.net_weight || 'N/A',
      item?.nalco_rate || 'N/A',
      item?.packed_weight || 'N/A',
      item?.dispatched_weight || 'N/A',
    ]);
    doc.autoTable({
      head: [pdfColumns],
      body: rows,
      startY: y + 100,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 5,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: false,
        textColor: [0, 0, 0],
        fontSize: 9,
      },
      bodyStyles: {
        fontSize: 8,
      },
      rowPageBreak: 'avoid',
      didDrawPage: data => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `Page ${pageCount}`,
          doc.internal.pageSize.width - 30,
          doc.internal.pageSize.height - 10,
        );
      },
    });
    const pdfBlob = doc.output('blob');
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0px';
    iframe.style.bottom = '0px';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.border = 'none';
    iframe.src = URL.createObjectURL(pdfBlob);
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };

    dispatch(
      createActivityLog({
        action: 'PRINT',
        module_name: 'Proforma',
        ip_address: ipAddress,
        discription: 'Proforma print download successfully!',
      }),
    );
  };

  const [isLoading, setIsLoading] = useState(false);

  const handlePrintDownload = async () => {
    const count = totalRecord;
    if (count > 0) {
      try {
        setIsLoading(true);
        const response = await dispatch(
          fetchProformaList({
            page: 1,
            pagesize: count,
            ...(search && { search }),
            ...(orderBy && { ordering: orderBy }),
          }),
        ).unwrap();

        const proformaDetails = response?.results?.data;
        if (proformaDetails && proformaDetails.length > 0) {
          const createPDF = (includePrintText, pdfData) => {
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            pdf.addFileToVFS('WorkSans-normal.ttf', font);
            pdf.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
            pdf.setFont('WorkSans');

            const pdfColumns = [
              { header: 'Sr No.', dataKey: 'srNo', field: 'srNo' },
              {
                header: 'Proforma Date',
                dataKey: 'proforma_date',
                field: 'proforma_date',
              },
              {
                header: 'Proforma No.',
                dataKey: 'proforma_no',
                field: 'proforma_no',
              },
              {
                header: 'Work Order No.',
                dataKey: 'workorder_order_no',
                field: 'workorder_order_no',
              },
              {
                header: 'Customer Name	',
                dataKey: 'workorder_customer_name',
                field: 'workorder_customer_name',
              },
              {
                header: 'Freight Charges	',
                dataKey: 'freight_charges',
                field: 'freight_charges',
              },
              {
                header: 'Created At',
                dataKey: 'created_at',
                field: 'created_at',
              },
              {
                header: 'Created By',
                dataKey: 'created_by',
                field: 'created_by',
              },
              {
                header: 'Updated At',
                dataKey: 'updated_at',
                field: 'updated_at',
              },
              {
                header: 'Updated By',
                dataKey: 'updated_by',
                field: 'updated_by',
              },
            ];
            const pdffilteredColumns = pdfColumns.filter(column =>
              checkedList.includes(column.dataKey),
            );

            const rows = pdfData.map((item, index) => {
              return {
                srNo: index + 1,
                proforma_date: item?.proforma_date
                  ? dayjs(item.proforma_date).format('DD-MM-YYYY')
                  : 'N/A',
                proforma_no: item?.proforma_no || 'N/A',
                workorder_order_no: item?.workorder_no || 'N/A',
                workorder_customer_name: item?.customer_name || 'N/A',
                freight_charges: item?.freight_charges || 'N/A',
                created_at: item?.created_at
                  ? moment(item.created_at).format('DD-MM-YYYY')
                  : 'N/A',
                created_by: item?.created_by
                  ? `${item.created_by.first_name} ${item.created_by.last_name}`
                  : 'N/A',
                updated_at: item?.updated_at
                  ? moment(item.updated_at).format('DD-MM-YYYY')
                  : 'N/A',
                updated_by: item?.updated_by
                  ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
                  : 'N/A',
              };
            });

            pdf.text('Proforma', pdf.internal.pageSize.width / 2, 40, {
              align: 'center',
            });
            pdf.autoTable({
              head: [pdffilteredColumns.map(col => col.header)],
              body: rows.map(row =>
                pdffilteredColumns.map(col => row[col.dataKey]),
              ),
              startY: 65,
              theme: 'grid',
              styles: {
                font: 'times',
                halign: 'center',
                overflow: 'linebreak',
                cellPadding: 3.5,
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                textColor: [0, 0, 0],
              },
              headStyles: {
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                fillColor: false,
              },
              tableLineColor: [0, 0, 0],
              rowPageBreak: 'avoid',
              margin: { top: 65, left: 20, right: 20, bottom: 30 },
              didDrawPage: data => {
                const pageNumber = pdf.internal.getNumberOfPages();
                const str = `Page ${pageNumber}`;
                const dateStr = `${new Date().toLocaleString()}`;
                pdf.setFontSize(10);
                pdf.text(dateStr, 40, pageHeight - 20);
                pdf.text(str, pageWidth - 60, pageHeight - 20);
                if (pageNumber === 1) {
                  pdf.addImage(logo, 'PNG', pageWidth - 100, 10, 90, 30);
                }
              },
            });

            return pdf;
          };

          /* PDF for Print Preview */
          const printPDF = createPDF(true, proformaDetails);
          const pdfBlob = printPDF.output('blob');

          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed';
          iframe.style.right = '0px';
          iframe.style.bottom = '0px';
          iframe.style.width = '1px';
          iframe.style.height = '1px';
          iframe.style.border = 'none';
          iframe.src = URL.createObjectURL(pdfBlob);

          document.body.appendChild(iframe);

          iframe.onload = () => {
            setIsLoading(false); // Stop Loading after iframe loads
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          };
          dispatch(
            createActivityLog({
              action: 'PRINT',
              module_name: 'Proforma',
              ip_address: ipAddress,
              discription: 'Proforma print download successfully!',
            }),
          )
        } else {
          setIsLoading(false);
          console.warn('No work order details found.');
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Failed to fetch or generate PDF:', error);
      }
    }
  };

  return (
    <div className='mainwrap'>
      <div className="sticyaction">
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>
            <h2>Proforma's</h2>
            <div className="bredcrumbwrp">
              <Link
                className="back"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: `Proforma's` },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={inputValue}
                style={{ width: 190 }}
                onChange={handleSearch}
              />
              <Link>
                <Tooltip title="Filter">
                  <Button
                    type="primary"
                    disabled={!search}
                    className="iconlink"
                    onClick={resetFilter}
                  >
                    <FilterIcon />
                  </Button>
                </Tooltip>
              </Link>
              {currentUser.permissions.permissions.proforma.includes(
                'print_proforma_pdf_copy',
              ) && (
                  <>
                    <Tooltip placement="top" title={'Print'}>
                      <Button
                        type="primary"
                        className="iconlink"
                        onClick={handlePrintDownload}
                      >
                        <PrintIcons />
                      </Button>
                    </Tooltip>

                  </>
                )}
              {currentUser.permissions.permissions.proforma.includes(
                'download_proforma_excel_copy',
              ) && (
                  <Tooltip
                    placement="top"
                    title={'Export Excel'}
                    onClick={activityLog}
                  >
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                    >
                      <CSVLink
                        filename={'Proforma.csv'}
                        className="iconlink"
                        style={{ width: '10px' }}
                        data={csvData}
                        headers={csvFilterheaders}
                      >
                        <FileExcelOutlined />
                      </CSVLink>
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.proforma.includes(
                'add_proforma',
              ) && (
                  <Link to="/create-proforma">
                    <Tooltip title="Create Proforma">
                      <Button type="primary" icon={<PlusOutlined />}>
                        Create Proforma
                      </Button>
                    </Tooltip>
                  </Link>
                )}
              {currentUser.permissions.permissions.proforma.includes(
                'view_proforma',
              ) && (
                  <Link to="/archive-proforma-list" className="iconlink">
                    <Tooltip title="Archive">
                      <Archiveicon />
                    </Tooltip>
                  </Link>
                )}
              <div className="gridiconswrap">
                <Tooltip placement="topLeft" title="Column Filter Options">
                  <Dropdown
                    overlay={columnMenu}
                    trigger={['click']}
                    placement="bottomRight"
                    visible={dropdownVisible}
                    onVisibleChange={setDropdownVisible}
                  >
                    <Button
                      type="primary"
                      icon={<ColumnList />}
                      onClick={e => e.preventDefault()}
                    />
                  </Dropdown>
                </Tooltip>
              </div>
            </Space>
          </Col>
        </Row>
      </div>

      <div className='tablewrp'>
        <Table
          rowKey="id"
          columns={dynamicColumns}
          dataSource={dataSource}
          pagination={false}
          loading={status === 'loading'}
          scroll={{ x: '600px' }}
          onChange={handleTableChange}
        />
      </div>


      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={['20', '50', '100']}
        showTotal={total => `Total ${total} items`}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />

    </div>
  );
}

export default ProformaList;
